import { IconPanelRightOpen } from "@/assets/icons/geist/IconPanelRightOpen";
import { withGitpodSize } from "@/assets/icons/utils";
import IconBranch from "central-icons/IconBranch";
import IconBubble2 from "central-icons/IconBubble2";
import IconCodeBrackets from "central-icons/IconCodeBrackets";
import IconLayoutSidebar from "central-icons/IconLayoutSidebar";
import { AgentExecutionDropdownMenu } from "@/components/ai/AgentExecutionDropdownMenu";
import { useReviewComments, formatReviewCommentsForAgent } from "@/components/ai/review/use-review-comments";
import type { ReviewComment, PendingCommentSelection } from "@/components/ai/review/types";
import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogBody,
} from "@/components/podkit/dialog/Dialog";
import { KeyboardCombo } from "@/components/keyboard/KeyboardCombo";
import { cn } from "@/components/podkit/lib/cn";
import { ConversationTabButton } from "@/routes/environments/details/ConversationTabButton";
import { ConversationTabPanel } from "@/routes/environments/details/ConversationTabPanel";
import { DetailsTabPanel } from "@/routes/environments/details/DetailsTabPanel";
import { ReviewTabPanel } from "@/routes/environments/details/ReviewTabPanel";
import { CodeTabPanel } from "@/routes/environments/details/CodeTabPanel";

import { Tooltip } from "@/components/Tooltip";
import { useDetailsPanelToggleShortcut } from "@/hooks/use-details-panel-toggle-shortcut";
import { useFlagReviewCommentsEnabled } from "@/hooks/use-flag";
import type { FollowModeState } from "@/hooks/use-follow-mode";
import useIsMobileViewport from "@/hooks/use-is-mobile-viewport";
import { useOptimisticPendingMessages } from "@/hooks/use-pending-messages";
import { useStreamConversation } from "@/hooks/use-stream-conversation";
import { useStuckConversationDetection } from "@/hooks/use-stuck-conversation-detection";
import type { PlainAgentExecution } from "@/queries/agent-queries";
import { useSendToAgentExecution } from "@/queries/agent-queries";
import { useEnvironment, useStartEnvironment } from "@/queries/environment-queries";
import { AgentExecution_Phase } from "gitpod-next-api/gitpod/v1/agent_pb";
import { EnvironmentGitStatus } from "gitpod-next-api/gitpod/v1/environment_pb";
import { agentExecutionName } from "@/utils/agent-executions";
import { ToggleDetailsPanelKeyBinding } from "@/utils/platform";
import { IconSidebarSimpleRightSquare } from "central-icons/IconSidebarSimpleRightSquare";

import {
    SendToAgentExecutionRequest,
    UserInputBlock,
    UserInputBlock_TextInput,
} from "gitpod-next-api/gitpod/v1/agent_pb";
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, type FC } from "react";
import { v4 as uuidv4 } from "uuid";

const SidebarIconClosed = withGitpodSize(IconSidebarSimpleRightSquare);

/**
 * Tab types for the conversation panel.
 * - "conversation": Chat with the agent (always available)
 * - "review": Review comments on code changes (always available when review_comments_enabled)
 * - "code": VS Code browser view (desktop only when review_comments_enabled)
 * - "details": Environment details (mobile only when review_comments_enabled, replaces "code" tab)
 */
type ConversationTab = "conversation" | "review" | "code" | "details";

export type EnvironmentAgentExecutionConversationRef = {
    switchToReviewTab: () => void;
    setPendingSelection: (selection: PendingCommentSelection | null) => void;
};

export type EnvironmentAgentExecutionConversationProps = {
    agentExecution: PlainAgentExecution | null;
    isDetailsPanelOpen: boolean;
    onToggleDetailsPanel: () => void;
    followMode?: FollowModeState;
    environmentId?: string;
    /** Callback when user wants to navigate to a comment in the diff */
    onNavigateToComment?: (comment: ReviewComment) => void;
    /** URL for VS Code browser view (when review feature is enabled) */
    vscodeUrl?: string;
};

export const EnvironmentAgentExecutionConversation = React.forwardRef<
    EnvironmentAgentExecutionConversationRef,
    EnvironmentAgentExecutionConversationProps
>(
    (
        {
            agentExecution,
            isDetailsPanelOpen,
            onToggleDetailsPanel,
            followMode,
            environmentId,
            onNavigateToComment,
            vscodeUrl,
        },
        ref,
    ) => {
        const { pendingMessages, handleNewMessage } = useOptimisticPendingMessages();
        const { error, messages, loadingMessage, lastAgentBlockContext } = useStreamConversation(
            agentExecution?.id,
            agentExecution?.status?.conversationUrl,
            pendingMessages,
        );
        const sendToAgentExecution = useSendToAgentExecution();
        const { data: environment } = useEnvironment(environmentId);
        const { mutate: startEnvironment, isPending: isStartingEnvironment } = useStartEnvironment();

        const { value: isReviewCommentsEnabled } = useFlagReviewCommentsEnabled();
        const { isMobileViewport: isMobile } = useIsMobileViewport();

        const handleStartEnvironment = useCallback(() => {
            if (environmentId) {
                startEnvironment({ environmentId });
            }
        }, [environmentId, startEnvironment]);

        const {
            comments,
            isReviewTabVisible,
            updateComment,
            removeComment,
            clearAllComments,
            dismissReviewTab,
            addComment,
            isAnnotationDismissed,
            updateCodeContext,
        } = useReviewComments(environmentId || "");

        const [activeTab, setActiveTab] = useState<ConversationTab>("conversation");
        const [isSubmittingReview, setIsSubmittingReview] = useState(false);
        const [pendingSelection, setPendingSelection] = useState<PendingCommentSelection | null>(null);
        const [showDismissDialog, setShowDismissDialog] = useState(false);

        // Track processed annotations to avoid re-running effect when comments change
        const processedAnnotationsRef = useRef<Set<string>>(new Set());

        // Reset to conversation tab when viewport changes and current tab is no longer available
        // Mobile shows "details" instead of "code", desktop shows "code" instead of "details"
        useEffect(() => {
            if (isReviewCommentsEnabled) {
                if (isMobile && activeTab === "code") {
                    setActiveTab("conversation");
                } else if (!isMobile && activeTab === "details") {
                    setActiveTab("conversation");
                }
            }
        }, [isMobile, isReviewCommentsEnabled, activeTab]);

        // Expose switchToReviewTab and setPendingSelection via ref
        useImperativeHandle(
            ref,
            () => ({
                switchToReviewTab: () => {
                    setActiveTab("review");
                },
                setPendingSelection: (selection: PendingCommentSelection | null) => {
                    setPendingSelection(selection);
                    if (selection) {
                        setActiveTab("review");
                    }
                },
            }),
            [],
        );

        // Detect stuck conversations and report to Sentry.
        useStuckConversationDetection(agentExecution, lastAgentBlockContext);

        useDetailsPanelToggleShortcut(() => {
            onToggleDetailsPanel();
        });

        // Convert agent code annotations to review comments (only when feature flag is enabled)
        useEffect(() => {
            if (!isReviewCommentsEnabled || !messages || !environmentId) return;

            for (const message of messages) {
                if (message.type !== "agent") continue;
                for (const fragment of message.fragments) {
                    if (fragment.type === "codeAnnotation") {
                        const annotation = fragment;

                        // Skip if already processed in this session
                        if (processedAnnotationsRef.current.has(annotation.id)) continue;

                        // Skip if this annotation was previously dismissed by the user
                        if (isAnnotationDismissed(annotation.id)) {
                            processedAnnotationsRef.current.add(annotation.id);
                            continue;
                        }

                        // Mark as processed before adding to prevent re-processing
                        processedAnnotationsRef.current.add(annotation.id);
                        addComment(
                            annotation.filePath,
                            annotation.startLine,
                            annotation.endLine,
                            annotation.description,
                            "agent",
                            annotation.id,
                        );
                    }
                }
            }
        }, [isReviewCommentsEnabled, messages, environmentId, addComment, isAnnotationDismissed]);

        // Auto-switch to review tab when comments are added
        useEffect(() => {
            if (isReviewTabVisible && comments.length > 0) {
                setActiveTab("review");
            }
        }, [isReviewTabVisible, comments.length]);

        const handleNavigateToComment = useCallback(
            (comment: ReviewComment) => {
                // Switch to review tab and scroll to the comment
                setActiveTab("review");
                // Try to scroll to the comment element
                setTimeout(() => {
                    const commentElement = document.getElementById(`review-comment-${comment.id}`);
                    commentElement?.scrollIntoView({ behavior: "smooth", block: "center" });
                }, 100);
                if (onNavigateToComment) {
                    onNavigateToComment(comment);
                }
            },
            [onNavigateToComment],
        );

        const handleSubmitReview = useCallback(
            async (selectedComments: ReviewComment[], additionalContext?: string) => {
                if (!agentExecution?.id || selectedComments.length === 0) return;

                setIsSubmittingReview(true);
                try {
                    const reviewMessage = formatReviewCommentsForAgent(selectedComments, additionalContext);
                    await sendToAgentExecution.mutateAsync(
                        new SendToAgentExecutionRequest({
                            agentExecutionId: agentExecution.id,
                            userInput: new UserInputBlock({
                                id: uuidv4(),
                                input: {
                                    case: "text",
                                    value: new UserInputBlock_TextInput({
                                        content: reviewMessage,
                                    }),
                                },
                            }),
                        }),
                    );
                    // Remove submitted comments
                    for (const comment of selectedComments) {
                        removeComment(comment.id);
                    }
                    // Switch to conversation if no comments left
                    if (selectedComments.length === comments.length) {
                        setActiveTab("conversation");
                    }
                } catch (error) {
                    console.error("Failed to submit review:", error);
                } finally {
                    setIsSubmittingReview(false);
                }
            },
            [agentExecution?.id, comments.length, sendToAgentExecution, removeComment],
        );

        const handleDismissReview = useCallback(() => {
            setShowDismissDialog(true);
        }, []);

        const handleConfirmDismiss = useCallback(() => {
            clearAllComments();
            dismissReviewTab();
            setActiveTab("conversation");
            setShowDismissDialog(false);
        }, [clearAllComments, dismissReviewTab]);

        const handleCancelDismiss = useCallback(() => {
            setShowDismissDialog(false);
        }, []);

        const gitStatus = environment?.status?.content?.git;
        const hasChanges = Boolean(gitStatus?.changedFiles.length);
        const agentPhase = agentExecution?.status?.phase;
        const isAgentBusy =
            agentPhase !== undefined &&
            agentPhase !== AgentExecution_Phase.WAITING_FOR_INPUT &&
            agentPhase !== AgentExecution_Phase.STOPPED;

        const handleStartReview = useCallback(async () => {
            if (!agentExecution?.id || !gitStatus?.changedFiles.length) return;

            const fileList = gitStatus.changedFiles.map((f) => `- ${f.path}`).join("\n");
            const reviewCommand = `/ona:review\n\nFiles changed:\n${fileList}`;

            try {
                await sendToAgentExecution.mutateAsync(
                    new SendToAgentExecutionRequest({
                        agentExecutionId: agentExecution.id,
                        userInput: new UserInputBlock({
                            id: uuidv4(),
                            input: {
                                case: "text",
                                value: new UserInputBlock_TextInput({
                                    content: reviewCommand,
                                }),
                            },
                        }),
                    }),
                );
                setActiveTab("conversation");
            } catch (error) {
                console.error("Failed to start review:", error);
            }
        }, [agentExecution?.id, gitStatus?.changedFiles, sendToAgentExecution]);

        // Show tabs when feature flag is enabled
        const showTabs = isReviewCommentsEnabled;

        return (
            <div
                data-testid="environment-agent-execution-conversation"
                className={cn("flex h-full min-w-[100px] flex-col rounded-l-lg border-border-base bg-surface-01")}
            >
                {/* Dismiss review confirmation dialog */}
                <Dialog open={showDismissDialog} onOpenChange={(open) => !open && handleCancelDismiss()}>
                    <DialogContent data-track-location="review_dismiss_dialog">
                        <DialogHeader>
                            <DialogTitle>Dismiss review?</DialogTitle>
                        </DialogHeader>
                        <DialogBody>
                            <p className="text-sm text-content-secondary">
                                Are you sure you want to dismiss this review? All comments will be lost.
                            </p>
                        </DialogBody>
                        <DialogFooter>
                            <Button variant="secondary" onClick={handleCancelDismiss} data-tracking-id="cancel-dismiss">
                                Cancel
                            </Button>
                            <Button
                                variant="destructive"
                                onClick={handleConfirmDismiss}
                                data-tracking-id="confirm-dismiss"
                            >
                                Dismiss
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
                <Header
                    agentExecution={agentExecution}
                    isDetailsPanelOpen={isDetailsPanelOpen}
                    onToggleDetailsPanel={onToggleDetailsPanel}
                    environmentId={environmentId}
                    showTabs={showTabs}
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                    reviewCommentCount={comments.length}
                    isReviewCommentsEnabled={isReviewCommentsEnabled}
                />
                {/* Conversation content - always mounted, visibility controlled by CSS */}
                <ConversationTabPanel
                    error={error}
                    loadingMessage={loadingMessage}
                    messages={messages}
                    agentExecution={agentExecution}
                    environmentId={environmentId}
                    followMode={followMode}
                    onNewMessage={handleNewMessage}
                    isHidden={showTabs && activeTab !== "conversation"}
                />
                {/* Review tab - mounted when showTabs is true, hidden via CSS when not active.
                Using CSS visibility instead of conditional rendering to preserve state when switching tabs. */}
                {showTabs && environment && (
                    <ReviewTabPanel
                        environment={environment}
                        comments={comments}
                        onEditComment={updateComment}
                        onRemoveComment={removeComment}
                        onNavigateToComment={handleNavigateToComment}
                        onSubmitReview={handleSubmitReview}
                        onDismissReview={handleDismissReview}
                        onStartReview={hasChanges ? handleStartReview : undefined}
                        isStartReviewDisabled={isAgentBusy || sendToAgentExecution.isPending}
                        isSubmitting={isSubmittingReview}
                        pendingSelection={pendingSelection}
                        onAddComment={(filePath, startLine, endLine, content) => {
                            addComment(filePath, startLine, endLine, content, "user");
                        }}
                        onClearPendingSelection={() => setPendingSelection(null)}
                        onUpdateCodeContext={updateCodeContext}
                        gitStatus={gitStatus ? new EnvironmentGitStatus(gitStatus) : undefined}
                        onLineSelectionForComment={setPendingSelection}
                        isHidden={activeTab !== "review"}
                    />
                )}
                {/* Code tab - VS Code browser view (desktop only when review_comments_enabled) */}
                {showTabs && (
                    <CodeTabPanel
                        vscodeUrl={vscodeUrl}
                        followMode={followMode}
                        isVisible={activeTab === "code"}
                        environment={environment}
                        onStartEnvironment={handleStartEnvironment}
                        isStartingEnvironment={isStartingEnvironment}
                    />
                )}
                {/* Details tab - environment details (mobile only when review_comments_enabled) */}
                {showTabs && environmentId && isMobile && isReviewCommentsEnabled && (
                    <DetailsTabPanel environmentId={environmentId} isHidden={activeTab !== "details"} />
                )}
            </div>
        );
    },
);
EnvironmentAgentExecutionConversation.displayName = "EnvironmentAgentExecutionConversation";

const Header: FC<{
    agentExecution: PlainAgentExecution | null;
    isDetailsPanelOpen: boolean;
    onToggleDetailsPanel: () => void;
    environmentId?: string;
    showTabs?: boolean;
    activeTab?: ConversationTab;
    onTabChange?: (tab: ConversationTab) => void;
    reviewCommentCount?: number;
    isReviewCommentsEnabled?: boolean;
}> = ({
    agentExecution,
    isDetailsPanelOpen,
    onToggleDetailsPanel,
    showTabs,
    activeTab,
    onTabChange,
    reviewCommentCount = 0,
    isReviewCommentsEnabled = false,
}) => {
    const displayName = useMemo(
        () => (agentExecution ? agentExecutionName(agentExecution) : "Conversation"),
        [agentExecution],
    );
    const { isMobileViewport: isMobile } = useIsMobileViewport();

    // On mobile with review_comments_enabled, show Details tab instead of Code tab
    const showDetailsTabInsteadOfCode = isMobile && isReviewCommentsEnabled;

    return (
        <div className="flex h-12 w-full items-center justify-between gap-2 border-b border-border-subtle px-4 py-2">
            <div className="flex h-6 min-w-0 flex-1 items-center gap-0.5">
                {agentExecution ? (
                    <AgentExecutionDropdownMenu agentExecution={agentExecution} />
                ) : (
                    <div className="flex min-w-0 items-center gap-1 rounded-md p-1">
                        <span className="min-w-0 truncate text-left text-base font-bold">{displayName}</span>
                    </div>
                )}
            </div>
            {showTabs && (
                <div
                    role="tablist"
                    aria-label="View options"
                    className="flex items-center gap-0.5 rounded-lg border-0.5 border-border-base bg-surface-button-tab-base p-0.5"
                >
                    <ConversationTabButton
                        id="conversation-tab"
                        label="Conversation"
                        icon={<IconBubble2 size={16} />}
                        isActive={activeTab === "conversation"}
                        onClick={() => onTabChange?.("conversation")}
                        panelId="conversation-panel"
                        hideLabel={isMobile}
                        data-tracking-id="conversation-tab"
                    />
                    <ConversationTabButton
                        id="review-tab"
                        label="Review"
                        icon={<IconBranch size={16} />}
                        isActive={activeTab === "review"}
                        onClick={() => onTabChange?.("review")}
                        panelId="review-panel"
                        badge={isMobile ? undefined : reviewCommentCount}
                        hideLabel={isMobile}
                        data-tracking-id="review-tab"
                    />
                    {showDetailsTabInsteadOfCode ? (
                        <ConversationTabButton
                            id="details-tab"
                            label="Details"
                            icon={<IconLayoutSidebar size={16} />}
                            isActive={activeTab === "details"}
                            onClick={() => onTabChange?.("details")}
                            panelId="details-panel"
                            hideLabel={isMobile}
                            data-tracking-id="details-tab"
                        />
                    ) : (
                        <ConversationTabButton
                            id="code-tab"
                            label="Code"
                            icon={<IconCodeBrackets size={16} />}
                            isActive={activeTab === "code"}
                            onClick={() => onTabChange?.("code")}
                            panelId="code-panel"
                            hideLabel={isMobile}
                            data-tracking-id="code-tab"
                        />
                    )}
                </div>
            )}
            {/* Show toggle button only when details panel is collapsed - hidden on mobile with review_comments_enabled */}
            {!isDetailsPanelOpen && !(isMobile && isReviewCommentsEnabled) && (
                <div className="flex items-center justify-end gap-1">
                    {isMobile ? (
                        <Tooltip content="Show environment details">
                            <Button
                                variant="ghost"
                                className="rounded-[4px] border-0 p-0"
                                size="lg"
                                onClick={onToggleDetailsPanel}
                                LeadingIcon={IconPanelRightOpen}
                                aria-label="Show environment details"
                                data-tracking-id="show-environment-details-agent-execution-conversation"
                            />
                        </Tooltip>
                    ) : (
                        <Tooltip
                            content={
                                <div className="flex flex-row items-center gap-2">
                                    <span>Show details panel</span>
                                    <KeyboardCombo
                                        keys={ToggleDetailsPanelKeyBinding}
                                        className="bg-surface-invert-tertiary"
                                    />
                                </div>
                            }
                        >
                            <Button
                                variant="ghost"
                                className="rounded-[4px] border-0 p-2"
                                size="sm"
                                onClick={onToggleDetailsPanel}
                                aria-label="Show right panel"
                                data-tracking-id="toggle-details-panel-agent-execution-conversation"
                            >
                                <SidebarIconClosed size="sm" />
                            </Button>
                        </Tooltip>
                    )}
                </div>
            )}
        </div>
    );
};
